import { deepEqualIgnoreUndefined } from '../../../../../helpers/deepEqual'
import {
  PerformanceCondition,
  PerformanceConditionGroups,
  PerformanceConditionType,
  PerformanceOperator,
  PerformanceRuleKind,
  PerformanceSettingData,
} from '../../types/types'

export const defaultPerformanceMarketIso = 'us'

export const performanceConditionTypeLabelKeys: Record<PerformanceConditionType, string> = {
  gps: 'internal-service-management:settings_performance_type_gps',
  rank: 'internal-service-management:settings_performance_type_rank',
}

export const performanceOperatorLabelKeys: Record<PerformanceOperator, string> = {
  '>': 'internal-service-management:settings_performance_operator_gt',
  '>=': 'internal-service-management:settings_performance_operator_gte',
  '<': 'internal-service-management:settings_performance_operator_lt',
  '<=': 'internal-service-management:settings_performance_operator_lte',
  '==': 'internal-service-management:settings_performance_operator_eq',
}

export const newPerformanceCondition = (): PerformanceCondition => ({ type: 'gps', operator: '>', value: '' })

export type PerformanceConditionPath = { marketIso: string; kind: PerformanceRuleKind; groupIndex: number }

export const getConditionGroups = (data: PerformanceSettingData, marketIso: string, kind: PerformanceRuleKind): PerformanceConditionGroups =>
  data[marketIso]?.[kind] ?? []

const withGroups = (
  data: PerformanceSettingData,
  marketIso: string,
  kind: PerformanceRuleKind,
  update: (groups: PerformanceConditionGroups) => PerformanceConditionGroups
): PerformanceSettingData => ({
  ...data,
  [marketIso]: { ...data[marketIso], [kind]: update(getConditionGroups(data, marketIso, kind)) },
})

export const addConditionGroup = (data: PerformanceSettingData, marketIso: string, kind: PerformanceRuleKind): PerformanceSettingData =>
  withGroups(data, marketIso, kind, (groups) => [...groups, [newPerformanceCondition()]])

export const addCondition = (data: PerformanceSettingData, { marketIso, kind, groupIndex }: PerformanceConditionPath): PerformanceSettingData =>
  withGroups(data, marketIso, kind, (groups) => groups.map((group, index) => (index === groupIndex ? [...group, newPerformanceCondition()] : group)))

export const updateCondition = (
  data: PerformanceSettingData,
  { marketIso, kind, groupIndex }: PerformanceConditionPath,
  conditionIndex: number,
  change: Partial<PerformanceCondition>
): PerformanceSettingData =>
  withGroups(data, marketIso, kind, (groups) =>
    groups.map((group, index) =>
      index === groupIndex ? group.map((condition, candidate) => (candidate === conditionIndex ? { ...condition, ...change } : condition)) : group
    )
  )

// Removes only that condition, and the group with it when it was the last one.
export const removeCondition = (
  data: PerformanceSettingData,
  { marketIso, kind, groupIndex }: PerformanceConditionPath,
  conditionIndex: number
): PerformanceSettingData =>
  withGroups(data, marketIso, kind, (groups) =>
    groups
      .map((group, index) => (index === groupIndex ? group.filter((_, candidate) => candidate !== conditionIndex) : group))
      .filter((group) => group.length > 0)
  )

export const getPerformanceValueError = (value: PerformanceCondition['value']): 'required' | 'invalid' | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? undefined : 'invalid'
  if (value === undefined || value === null || value.trim() === '') return 'required'
  return Number.isFinite(Number(value)) ? undefined : 'invalid'
}

const everyCondition = (data: PerformanceSettingData, predicate: (condition: PerformanceCondition) => boolean) =>
  Object.values(data).every((rules) => Object.values(rules ?? {}).every((groups) => (groups ?? []).every((group) => group.every(predicate))))

export const isPerformanceDataValid = (data: PerformanceSettingData): boolean => everyCondition(data, (condition) => !getPerformanceValueError(condition.value))

const mapConditions = (data: PerformanceSettingData, map: (condition: PerformanceCondition) => PerformanceCondition): PerformanceSettingData =>
  Object.fromEntries(
    Object.entries(data).map(([marketIso, rules]) => [
      marketIso,
      Object.fromEntries(Object.entries(rules ?? {}).map(([kind, groups]) => [kind, (groups ?? []).map((group) => group.map(map))])),
    ])
  )

// Edited values are strings; the stored ones are numbers.
export const toPerformanceSettingData = (data: PerformanceSettingData): PerformanceSettingData =>
  mapConditions(data, (condition) => ({ ...condition, value: typeof condition.value === 'number' ? condition.value : Number(condition.value) }))

export const isSamePerformanceData = (a: PerformanceSettingData, b: PerformanceSettingData): boolean =>
  deepEqualIgnoreUndefined(toPerformanceSettingData(a), toPerformanceSettingData(b))
